<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import FormScreenLoading from '@/shared/components/FormScreenLoading.vue'
import { useFormBusyState } from '@/shared/composables/useFormBusy'
import { useRouteBusyState } from '@/shared/composables/useRouteBusy'

const formBusy = useFormBusyState()
const routeBusy = useRouteBusyState()

const rawActive = computed(() => formBusy.show.value || routeBusy.show.value)
const labelKey = computed(() =>
  formBusy.show.value ? formBusy.labelKey.value : routeBusy.labelKey.value,
)

/** Sticky show: never drop for short gaps (route → page / modal prepare). */
const show = ref(false)
let shownAt = 0
let hideTimer: ReturnType<typeof setTimeout> | null = null
const MIN_VISIBLE_MS = 360
const HIDE_GRACE_MS = 120

watch(
  rawActive,
  (active) => {
    if (active) {
      if (hideTimer != null) {
        clearTimeout(hideTimer)
        hideTimer = null
      }
      if (!show.value) {
        show.value = true
        shownAt = Date.now()
      }
      return
    }

    if (!show.value) return
    const wait = Math.max(HIDE_GRACE_MS, MIN_VISIBLE_MS - (Date.now() - shownAt))
    if (hideTimer != null) clearTimeout(hideTimer)
    hideTimer = setTimeout(() => {
      hideTimer = null
      if (!rawActive.value) show.value = false
    }, wait)
  },
  { immediate: true },
)

/** Hide in-page spinners while the global overlay is up (avoids big+small stack). */
watch(
  show,
  (visible) => {
    document.body.classList.toggle('screen-busy', visible)
  },
  { immediate: true },
)
</script>

<template>
  <FormScreenLoading :show="show" :label-key="labelKey" />
</template>
